import { fmtUSD, safePnl } from '../../utils/formatters';
import { calcTradeStats } from '../../services/statsCalculator';

/**
 * MistakesTab — Mistake Cost Analysis.
 * Extracted from TradingJournal.js (Phase 3).
 */
const MistakesTab = ({
  darkMode,
  filteredTrades,
  closedFilteredTrades,
  trades,
}) => {
  return (
    <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
      <h2 className={`text-xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Mistake Cost Analysis
      </h2>
      <p className={`mb-6 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        See how much each trading mistake is costing you. Build discipline by understanding the true impact.
      </p>

      {filteredTrades.length === 0 ? (
        <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No trades found. Add some trades to see mistake analytics!
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overall Summary */}
          {(() => {
            const tradesWithMistakes = closedFilteredTrades.filter(t => t.mistakes && t.mistakes.length > 0);
            const tradesWithoutMistakes = closedFilteredTrades.filter(t => !t.mistakes || t.mistakes.length === 0);

            const withStats = calcTradeStats(tradesWithMistakes);
            const withoutStats = calcTradeStats(tradesWithoutMistakes);
            const pnlWithMistakes = withStats.totalPnL;
            const pnlWithoutMistakes = withoutStats.totalPnL;
            const winRateWithMistakes = withStats.winRate;
            const winRateWithoutMistakes = withoutStats.winRate;

            return (
              <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
              }`}>
                <h3 className={`font-semibold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mistakes Impact Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Trades with Mistakes
                    </div>
                    <div className="text-lg font-bold text-red-500">
                      {tradesWithMistakes.length}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({trades.length > 0 ? ((tradesWithMistakes.length / trades.length) * 100).toFixed(0) : 0}% of trades)
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      P&L with Mistakes
                    </div>
                    <div className={`text-lg font-bold ${pnlWithMistakes >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {pnlWithMistakes >= 0 ? '' : '-'}${fmtUSD(pnlWithMistakes)}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Win Rate: {winRateWithMistakes.toFixed(0)}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      P&L without Mistakes
                    </div>
                    <div className={`text-lg font-bold ${pnlWithoutMistakes >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {pnlWithoutMistakes >= 0 ? '' : '-'}${fmtUSD(pnlWithoutMistakes)}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Win Rate: {winRateWithoutMistakes.toFixed(0)}%
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Discipline Opportunity
                    </div>
                    {tradesWithMistakes.length > 0 ? (
                      <>
                        <div className={`text-lg font-bold ${winRateWithoutMistakes > winRateWithMistakes ? 'text-green-500' : 'text-gray-500'}`}>
                          +{(winRateWithoutMistakes - winRateWithMistakes).toFixed(0)}% WR
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Potential improvement
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-bold text-green-500">
                          ✓ Clean
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No mistakes logged
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Individual Mistake Breakdown */}
          <div>
            <h3 className={`font-semibold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Cost by Mistake Type
            </h3>

            {(() => {
              // Calculate stats for each mistake (closed trades only)
              const mistakeStats = {};

              closedFilteredTrades.forEach(trade => {
                if (trade.mistakes && trade.mistakes.length > 0) {
                  trade.mistakes.forEach(mistake => {
                    if (!mistakeStats[mistake]) {
                      mistakeStats[mistake] = {
                        trades: [],
                        totalPnL: 0,
                        occurrences: 0
                      };
                    }
                    mistakeStats[mistake].trades.push(trade);
                    mistakeStats[mistake].totalPnL += safePnl(trade);
                    mistakeStats[mistake].occurrences++;
                  });
                }
              });

              // Sort by total P&L (worst first - most costly mistakes)
              const sortedMistakes = Object.entries(mistakeStats)
                .map(([name, stats]) => {
                  const avgPnL = stats.occurrences > 0 ? stats.totalPnL / stats.occurrences : 0;
                  const winRate = stats.trades.length > 0
                    ? (stats.trades.filter(t => safePnl(t) > 0).length / stats.trades.length) * 100
                    : 0;
                  const mistakeTradesWithR = stats.trades.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);
                  const avgR = mistakeTradesWithR.length > 0
                    ? mistakeTradesWithR.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / mistakeTradesWithR.length
                    : 0;

                  return {
                    name,
                    occurrences: stats.occurrences,
                    totalPnL: stats.totalPnL,
                    avgPnL,
                    winRate,
                    avgR,
                    trades: stats.trades
                  };
                })
                .sort((a, b) => a.totalPnL - b.totalPnL); // Sort ascending (worst P&L first)

              if (sortedMistakes.length === 0) {
                return (
                  <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No mistakes logged yet. Keep tracking to identify patterns!
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  {sortedMistakes.map((mistake, index) => (
                    <div
                      key={mistake.name}
                      className={`p-4 rounded-lg border transition-colors duration-300 ${
                        darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-bold px-2 py-1 rounded ${
                            index === 0 ? 'bg-red-500/20 text-red-500' :
                            index === 1 ? 'bg-orange-500/20 text-orange-500' :
                            index === 2 ? 'bg-yellow-500/20 text-yellow-500' :
                            darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                          }`}>
                            #{index + 1}
                          </span>
                          <div>
                            <h4 className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {mistake.name}
                            </h4>
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {mistake.occurrences} occurrence{mistake.occurrences !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xl font-bold ${mistake.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {mistake.totalPnL >= 0 ? '+' : '-'}${fmtUSD(mistake.totalPnL)}
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total Cost
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg Cost/Trade
                          </div>
                          <div className={`font-semibold ${mistake.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {mistake.avgPnL >= 0 ? '+' : '-'}${fmtUSD(mistake.avgPnL)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Win Rate
                          </div>
                          <div className={`font-semibold ${mistake.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                            {mistake.winRate.toFixed(0)}%
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg R
                          </div>
                          <div className={`font-semibold ${mistake.avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {mistake.avgR >= 0 ? '+' : ''}{mistake.avgR.toFixed(2)}R
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            % of All Trades
                          </div>
                          <div className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {trades.length > 0 ? ((mistake.occurrences / trades.length) * 100).toFixed(0) : 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MistakesTab;
